// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import Link from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ExpressCheckoutButton from "@mcmc/App/Components/ExpressCheckoutButton";
import MiniCartLine from "@mcmc/App/Components/MiniCart/MiniCartLine";
import { isUnderCheckoutFlow } from "@mcmc/App/Store/Selectors/PageSelectors";
import DrawerContent from "@mcmc/XDL/Components/Common/DrawerContent";
import DrawerFooter from "@mcmc/XDL/Components/Common/DrawerFooter";
import DrawerLink from "@mcmc/XDL/Components/Common/DrawerLink";
import Notify from "@mcmc/XDL/Components/Notify";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;

    const isAuthenticated = getSession(state).isAuthenticated;
    const accountSettings = getSettingsCollection(state).accountSettings;
    const allowGuestCheckout = accountSettings.allowGuestCheckout;
    const preventUserActions = isUnderCheckoutFlow(state);

    return {
        isLoading: isCartLoading,
        cart,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled: preventUserActions || isCartLoading || !cart || isCartCheckoutDisabled(cart),
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        currentLocation: getLocation(state),
        shouldShowLoginForPricing: !isAuthenticated && !allowGuestCheckout && !isCartEmpty(cart),
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        preventUserActions,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    updateCartLine,
    removeCartLine,
};

interface MiniCartProps {
    isOpen: boolean;
    handleClose: () => void;
}

export interface MiniCartStyles {
    drawer?: DrawerPresentationProps;
    subtotalContainer?: InjectableCss;
    subtotalText?: TypographyPresentationProps;
}

export const miniCartStyles: MiniCartStyles = {
    drawer: {
        position: "right",
        cssOverrides: {
            drawerBody: css`
                ${getProp("theme.drawer.defaultProps.cssOverrides.drawerBody")};
                width: 475px;
            `,
        },
    },
    subtotalContainer: {
        css: css`
            display: flex;
            padding-top: 20px;
            margin-bottom: 30px;
            justify-content: space-between;
            border-top: 1px solid ${getColor("common.border")};
            flex-wrap: wrap;
        `,
    },
    subtotalText: {
        size: "18px",
        lineHeight: "21px",
        weight: "bold",
        css: css`
            letter-spacing: 0.21px;
        `,
    },
};

const styles = miniCartStyles;

type Props = MiniCartProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

const MiniCart = ({
    isOpen,
    handleClose,
    cart,
    isPunchOutOrder,
    history,
    checkoutShippingPageUrl,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    canCheckoutWithCart,
    cartUrl,
    updateCartLine,
    removeCartLine,
    isCartEmpty,
    currentLocation,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    isLoading,
    shouldShowLoginForPricing,
    signInPageUrl,
    preventUserActions,
}: Props) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);

    useEffect(() => {
        handleClose();
    }, [currentLocation]);

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            handleClose();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);
        preloadCheckoutShippingData({
            onSuccess: () => {
                // TODO ISC-12526 The checkout shipping page link does not exist
                // because the PageLinks response doesn't return it.
                history.push(checkoutShippingPageUrl!);
                handleClose();
            },
        });
    };

    const cartLinesDisplay = cart?.cartLines?.map(cartLine => {
        const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;
        return (
            <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                <MiniCartLine
                    cart={cart}
                    updateCartLine={updateCartLine}
                    removeCartLine={removeCartLine}
                    showRemoveAction={showRemoveAction && !preventUserActions}
                    disabled={preventUserActions}
                />
            </CartLineContext.Provider>
        );
    });

    const loginForPricingHandler = () => {
        if (signInPageUrl) {
            history.push(signInPageUrl);
        }
    };

    let checkoutLabel = cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout");
    if (isPunchOutOrder) {
        checkoutLabel = translate("Return to Requisition");
    }

    return (
        <>
            <Drawer
                isOpen={isOpen}
                handleClose={handleClose}
                headline={<DrawerLink href={cartUrl} onClick={handleClose} title={translate("Cart Summary")} />}
                {...(styles.drawer as DrawerProps)}
            >
                <DrawerContent>
                    <LoadingOverlay
                        loading={isLoading}
                        css={css`
                            width: 100%;
                        `}
                    >
                        {cartLinesDisplay}
                        {!isCartEmpty && cart && (
                            <StyledWrapper {...styles.subtotalContainer}>
                                <Typography {...styles.subtotalText}>
                                    {translate("Subtotal ({0} Items):", cart!.totalCountDisplay.toString())}&nbsp;
                                </Typography>
                                <Typography {...styles.subtotalText}>{cart!.orderSubTotalDisplay}</Typography>
                            </StyledWrapper>
                        )}
                        {isCartEmpty && (
                            <Notify messageType="info" hideCloseButton={true} body={siteMessage("Cart_NoOrderLines")} />
                        )}
                    </LoadingOverlay>
                </DrawerContent>
                <DrawerFooter>
                    {shouldShowLoginForPricing && (
                        <Button
                            onClick={loginForPricingHandler}
                            data-test-selector="miniCart_LoginForPricing"
                            variant="primary"
                            sizeVariant="medium"
                        >
                            {translate("Login For Your Price")}
                        </Button>
                    )}
                    {canCheckoutWithCart && !shouldShowLoginForPricing && (
                        <>
                            <Button
                                onClick={checkoutHandler}
                                disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                                data-test-selector="miniCart_Checkout"
                                variant="primary"
                                sizeVariant="medium"
                            >
                                {checkoutLabel}
                            </Button>
                            {!isPunchOutOrder && (
                                <ExpressCheckoutButton
                                    hasQuoteRequiredProducts={hasQuoteRequiredProducts}
                                    disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                                    data-test-selector="miniCart_ExpressCheckout"
                                />
                            )}
                        </>
                    )}
                    <Link href={cartUrl} onClick={handleClose}>
                        {translate("View Shopping Cart")}
                    </Link>
                </DrawerFooter>
            </Drawer>
            <TwoButtonModal
                headlineText=""
                modalIsOpen={quoteRequiredModalIsOpen}
                messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={() => setQuoteRequiredModalIsOpen(false)}
                onSubmit={checkoutHandler}
                submitTestSelector="QuoteInCartMessageSubmit"
            ></TwoButtonModal>
        </>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
